import React, { useEffect, useMemo, useState } from 'react';
import { collision, raycastWalls } from '../game/streetCollision';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { doorPose } from '../city/venueRegistry';
import { baseGroundY, onGround } from '../city/cityGround';
import NeonSign from './NeonSign';

// VenueFacades — the street face of each venue the town voted for: its door, the
// light over it, and its NAME above it (plan step 5: "the World adds the
// building, its door, its sign and its map label without a deploy").
//
// A registry venue stands on an existing city building (⚙ → Sites, phase 1), so
// the building is already there, solid and lit; what it lacks is anything that
// says this one is a club now. That is all this draws, at the door the site
// names, turned to the street (local +z = out, like every other site).
//
// HOURS (Khari's decision 4: "outside them it is closed and dark"): open, the
// lamp is warm, a line of light leaks under the door, a soft pool lies on the
// pavement and the name is lit in the venue's colour. Shut, all of it goes dark
// and the name is a shadow on the board — still readable up close, so you know
// what it is and the door tells you when it opens.
//
// ⚠ THIS WORLD IS NOT IN METRES: a person is ~0.75 units, so a door is ~0.95
// tall. No real lights (every point light costs every lit pixel, and the plaza
// already stuttered on a phone): everything here is unlit colour and one shared
// additive texture, and the bloom pass does the glow.

const DOOR = { w: 0.62, h: 0.95 };
const LAMP_Y = 1.06;
const SIGN_Y = 1.42;
const SIGN_H = 0.44;
const WARM = '#ffcf8a';
const DARK = '#15110e';

/* ON THE WALL, NOT IN FRONT OF IT (2026-11-03, Khari: "when doors and signs are
   attached to buildings, they are currently leaving a gap between themselves and
   the building" — the GangstaFuel neon, seen from the side, a body's width off
   the wall). The site's door point is its survey's (⚙ → Sites: the footprint's
   edge), and a building's real wall can stand back from that edge. So the wall is
   MEASURED: rays from in front of the door, back toward the building, against the
   street's own walls (game/streetCollision), at door height and at sign height —
   three across each, the middle one kept, so a lamp post in the way cannot pull
   the sign off the building. → how far to move along the door's facing (+ = out,
   − = back toward the building), or 0 when there is no wall to find. */
const PROBE_FROM = 1.6;      // start this far out in front of the door point
const PROBE_FAR = 5;         // and look this far back
const MAX_SHIFT = 3;
const _from = new THREE.Vector3(); const _dir = new THREE.Vector3();
function wallShift(p, y0, h) {
  if (!collision.bvh) return 0;
  const ox = Math.sin(p.rotY); const oz = Math.cos(p.rotY);       // out, to the street
  const sx = Math.cos(p.rotY); const sz = -Math.sin(p.rotY);      // along the wall
  const hits = [];
  for (const across of [-0.3, 0, 0.3]) {
    _from.set(p.x + ox * PROBE_FROM + sx * across, y0 + h, p.z + oz * PROBE_FROM + sz * across);
    _dir.set(-ox, 0, -oz);
    const hit = raycastWalls(_from, _dir, PROBE_FAR);
    if (hit) hits.push(PROBE_FROM - hit.distance);   // where the wall is, along `out`, from the door point
  }
  if (!hits.length) return 0;
  hits.sort((a, b) => a - b);
  const mid = hits[Math.floor(hits.length / 2)];
  return Math.abs(mid) > MAX_SHIFT ? 0 : mid;
}

// One soft round pool for every door in the city, made on first use.
let poolTex = null;
function pool() {
  if (poolTex) return poolTex;
  const c = document.createElement('canvas'); c.width = 64; c.height = 64;
  const g = c.getContext('2d');
  const grd = g.createRadialGradient(32, 32, 2, 32, 32, 32);
  grd.addColorStop(0, 'rgba(255,200,140,0.9)'); grd.addColorStop(0.5, 'rgba(255,170,100,0.3)'); grd.addColorStop(1, 'rgba(255,150,80,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 64, 64);
  poolTex = new THREE.CanvasTexture(c);
  poolTex.colorSpace = THREE.SRGBColorSpace;
  return poolTex;
}

function Facade({ slug, venue, ov, open, ground, walls }) {
  const room = ROOMS[slug];
  const accent = room?.accent || '#ffb25e';
  const p = doorPose(venue.site, ov);
  const y = useMemo(() => baseGroundY(p.x, p.z), [p.x, p.z, ground]); // eslint-disable-line react-hooks/exhaustive-deps
  // Onto the building's real wall (wallShift): the door at door height, the sign at its own.
  const doorAt = useMemo(() => wallShift(p, y, DOOR.h / 2), [p.x, p.z, p.rotY, y, walls]); // eslint-disable-line react-hooks/exhaustive-deps
  const signAt = useMemo(() => wallShift(p, y, SIGN_Y + 0.2), [p.x, p.z, p.rotY, y, walls]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (Math.abs(doorAt) > 0.03 || Math.abs(signAt) > 0.03) console.info(`[facade] ${slug}: door ${doorAt.toFixed(2)}, sign ${signAt.toFixed(2)} (units along its facing; − = back onto the wall)`);
  }, [slug, doorAt, signAt]);
  // The board fits the name, and never runs wider than the building's front.
  const signW = Math.min(Math.max(1.3, venue.name.length * 0.15 + 0.5), Math.max(1.3, Math.min(4.2, venue.site.w * 0.9)));
  return (
    <group position={[p.x, y, p.z]} rotation={[0, p.rotY, 0]}>
      <group position={[0, 0, doorAt]}>
      {/* The door: dark, with a frame in the venue's colour so it reads as a
          door on a facade that was not drawn with one. */}
      <mesh position={[0, DOOR.h / 2, 0.03]} raycast={() => null}>
        <planeGeometry args={[DOOR.w, DOOR.h]} />
        <meshBasicMaterial color="#0a0809" />
      </mesh>
      {[[-1, 0], [1, 0]].map(([sx]) => (
        <mesh key={sx} position={[sx * (DOOR.w / 2 + 0.02), DOOR.h / 2, 0.034]} raycast={() => null}>
          <planeGeometry args={[0.035, DOOR.h + 0.04]} />
          <meshBasicMaterial color={open ? accent : DARK} toneMapped={false} />
        </mesh>
      ))}
      <mesh position={[0, DOOR.h + 0.02, 0.034]} raycast={() => null}>
        <planeGeometry args={[DOOR.w + 0.075, 0.035]} />
        <meshBasicMaterial color={open ? accent : DARK} toneMapped={false} />
      </mesh>
      {/* Light leaking out under it — the API's own words for a new venue. */}
      {open && (
        <mesh position={[0, 0.025, 0.036]} raycast={() => null}>
          <planeGeometry args={[DOOR.w - 0.04, 0.03]} />
          <meshBasicMaterial color={WARM} toneMapped={false} />
        </mesh>
      )}
      {/* The lamp over the door, and the pool it would throw. */}
      <mesh position={[0, LAMP_Y, 0.07]} raycast={() => null}>
        <boxGeometry args={[0.22, 0.06, 0.1]} />
        <meshBasicMaterial color={open ? WARM : DARK} toneMapped={false} />
      </mesh>
      {open && (
        <mesh position={[0, 0.015, 0.75]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null} renderOrder={2}>
          <planeGeometry args={[2.0, 1.6]} />
          <meshBasicMaterial map={pool()} transparent opacity={0.55} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
        </mesh>
      )}
      </group>
      <group position={[0, 0, signAt]}>
      {/* A NEON LOGO, where the room has one (rooms.js `neonSign`; the GangstaFuel
          Gym's, 2026-11-03): the venue's own mark over the door, lit at dusk —
          in place of the lettered board. Its back is on the wall; it starts just
          above the lamp. */}
      {room?.neonSign?.url && (
        <group position={[0, LAMP_Y + 0.1, 0]}>
          <NeonSign url={room.neonSign.url} color={room.neonSign.color || accent}
            width={room.neonSign.width || Math.min(2.4, Math.max(1.5, (venue.site.w || 3) * 0.7))} />
        </group>
      )}
      {/* THE NAME the town voted for, over the door, facing the street. Built
          like the stage's board (StageSign): a dark plate and drei Text. */}
      {!room?.neonSign?.url && (
      <group position={[0, SIGN_Y, 0.06]}>
        <mesh raycast={() => null}>
          <planeGeometry args={[signW, SIGN_H]} />
          <meshBasicMaterial color="#0c0a09" />
        </mesh>
        <mesh position={[0, -SIGN_H / 2 + 0.05, 0.004]} raycast={() => null}>
          <planeGeometry args={[signW * 0.78, 0.016]} />
          <meshBasicMaterial color={open ? accent : DARK} toneMapped={false} />
        </mesh>
        <Text
          position={[0, 0.03, 0.01]}
          fontSize={0.22}
          maxWidth={signW - 0.16}
          letterSpacing={0.06}
          color={open ? accent : '#3a322c'}
          anchorX="center"
          anchorY="middle"
          textAlign="center"
          outlineWidth={open ? 0.006 : 0}
          outlineColor="#000000"
        >
          {venue.name}
        </Text>
      </group>
      )}
      </group>
    </group>
  );
}

export default function VenueFacades() {
  const registry = useWorldStore((s) => s.venueRegistry);
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  const hours = useWorldStore((s) => s.venueOpen);
  // The ground's real heights land a few seconds after the city does.
  const [ground, setGround] = useState(0);
  useEffect(() => onGround(() => setGround((n) => n + 1)), []);
  // The street's walls are built after the city (game/streetCollision): measure again when they are.
  const [walls, setWalls] = useState(collision.version);
  useEffect(() => {
    const t = setInterval(() => { if (collision.version !== walls) setWalls(collision.version); }, 1000);
    return () => clearInterval(t);
  }, [walls]);
  // Not the one you are inside (2026-10-31): a sited room's windows would show
  // the back of its own door and sign standing in the doorway.
  const inside = useWorldStore((s) => (s.zoom === ZOOM.INTERIOR ? s.interior : null));
  const slugs = Object.keys(registry || {}).filter((k) => ROOMS[k]?.registry && !ROOMS[k].retired && k !== inside);
  if (!slugs.length) return null;
  return (
    <group>
      {slugs.map((k) => (
        <Facade key={k} slug={k} venue={registry[k]} ov={overrides?.[k] || null} open={hours?.[k]?.open !== false} ground={ground} walls={walls} />
      ))}
    </group>
  );
}
